import { Component, OnInit, OnDestroy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { combineLatest, Subject } from 'rxjs'
import { first, map, takeUntil } from 'rxjs/operators'
import * as _ from 'lodash'
import * as moment from 'moment-timezone'

import { Persona } from '../models/persona'
import { Membership } from '../models/membership'
import { SeMembershipCard, SeAvatarGroupAvatar } from '../models/membership-card-types'
import { Organization } from '../models/organization'
import { Mapper } from '../services/mapper.service'
import { environment } from '../../environments/environment'
import { MembershipService } from '../services/membership.service'
import { OrganizationService } from '../services/organization.service'
import { PersonaService } from '../services/persona.service'
import { DownloadParams, CardIdParams } from '../models/download'
import { SeAnalyticsService } from '../services/se-analytics.service'

interface MembershipBlock {
  card: SeMembershipCard,
  iosDownloadParams: DownloadParams,
  androidDownloadParams: DownloadParams
}

@Component({
  standalone: false,
  selector: 'app-post-assignments',
  templateUrl: './post-assignments.component.html',
  styleUrls: ['./post-assignments.scss']
})
export class PostAssignmentsComponent implements OnInit, OnDestroy {

  public persona: Persona
  public membershipBlocks: MembershipBlock[] = []
  public memberships: Membership[]
  public organization: Organization
  public affiliateOrganization: Organization
  public parentOrganization: Organization
  public loaded = false
  public buttonValue: string
  public mySeUrl: string
  public membershipStatus: string
  private membershipIds: string[]
  private unsubscribe: Subject<any> = new Subject()
  private orgAvatars: { [id: string]: SeAvatarGroupAvatar } = { }

  constructor(
    public acRoute: ActivatedRoute,
    private membershipService: MembershipService,
    private personaService: PersonaService,
    private organizationService: OrganizationService,
    private seAnalyticsService: SeAnalyticsService
  ) { }

  public ngOnInit(): void {
    this.initRouteListener()
    this.seAnalyticsService.pageView({
      depth1: 'Memberships',
      depth2: 'PostAssignments',
      depth3: 'Confirmation'
    })
  }

  public ngOnDestroy() {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  private initRouteListener(): void {
    combineLatest([this.acRoute.params, this.acRoute.queryParams])
      .pipe(takeUntil(this.unsubscribe))
      .pipe(map(results => ({ params: results[0], queryParams: results[1] })))
      .subscribe(({ params, queryParams }) => {
        this.membershipIds = queryParams.membershipIds || []
        this.initMemberships()
      })
  }

  private initMemberships(): void {
    this.membershipService.findAll(this.membershipIds)
      .subscribe(memberships => {
        this.memberships = memberships
        this.personaService.getPersona(memberships[0].persona_id)
        this.initPersona()
      })
  }

  private initOrganizations(orgIds: string[]) {
    this.organizationService.findAll(orgIds)
      .subscribe(data => {
        const orgs = Mapper.asArray<Organization>(data.result, Organization)
        this.organization = orgs[0]
        orgs.forEach(org => {
          if (!this.orgAvatars[org.id]) {
            this.orgAvatars[org.id] = new SeAvatarGroupAvatar({ identity: org.name, imgUrl: org.logo?.small_logo_url })
          }
        })
        this.membershipBlocks = this.memberships.map(membership => this.setupMembershipBlock(membership))
        this.initRedirectButton()
        this.loaded = true
      })
  }

  private initAffiliationOrganization(orgId: number): void {
    if (!orgId) return
    this.organizationService.find(`${orgId}`)
      .pipe(first())
      .subscribe(data => { this.affiliateOrganization = Mapper.as<Organization>(data.result, Organization) })
  }

  private initPersona() {
    this.personaService.current
      .subscribe((persona) => {
        this.persona = persona
        this.initOrganizations(_.uniq(this.memberships.map(mem => mem.membership_definition.boss_organization_id)))
        const affiliateOrgId = this.memberships.find(mem => mem.affiliation_organization_id)?.affiliation_organization_id
        this.initAffiliationOrganization(affiliateOrgId)
      })
  }

  private initRedirectButton(): void {
    this.buttonValue = `View My ${this.membershipBlocks.length > 1 ? 'Memberships' : 'Membership' }`
    this.mySeUrl = environment.mySeUrl + '/profile/' + this.persona.id
  }

  public redirect(): void {
    window.location.href = this.mySeUrl
  }

  public redirectToQuizzard(): void {
    window.location.href = `/org/${this.organization.slug}/affiliation/${this.affiliateOrganization.slug}/member-info/${this.persona.id}`
  }

  private setupMembershipBlock(membership: Membership): MembershipBlock {
    const membershipCard = new SeMembershipCard({ })

    membershipCard.member.number = membership.number.number
    membershipCard.memberships[0].name = membership.membership_definition?.name
    membershipCard.memberships[0].status = membership.membershipStatus === 'valid' ? 'paid' : membership.membershipStatus
    membershipCard.member.image = this.persona.profileImage('crop')
    membershipCard.member.name = this.persona.fullName
    membershipCard.memberships[0].effectiveAt = moment(membership.effective_at)
      .tz(this.organization.timezoneOrDefault).toISOString()
    membershipCard.memberships[0].expiresAt = moment(membership.expires_at)
      .tz(this.organization.timezoneOrDefault).toISOString()
    membershipCard.organizations = [this.orgAvatars[membership.membership_definition.boss_organization_id]]

    return {
      card: membershipCard,
      iosDownloadParams: new CardIdParams(membership.card_id, 'ios'),
      androidDownloadParams: new CardIdParams(membership.card_id, 'android')
    }
  }
}
